import { BattingStats, DataSection, PitchingStats, PitchReprocessed, ResultDataNew, ResultType, ScoringData }  from '../enums/index.js';
import {
    classifiesAsStrikeout as defaultStrikeOutClassifier,
    classifiesAsHit as defaultHitClassifier,
    classifiesAsPlateAppearance as defaultPlateAppearanceClassifier,
} from '../classificators/plateAppearanceResult/index.js';
import {
    classifiesAsSwing as defaultSwingClassifier,
    classifiesAsWhiff as defaultWhiffClassifier
} from '../classificators/pitchCall/index.js';
import defaultkZoneClassifier from '../classificators/kZoneClassifier.js';
import IterableDataProcessor from '../entities/IterableDataProcessor.js';
import PlateDisciplineStatsCalculator from './PlateDisciplineStatsCalculator.js';

/**
 * Calculates stats often named as Plate Discipline
 * @see https://www.fantraxhq.com/sabermetric-series-part-3-plate-discipline/
 * @see https://library.fangraphs.com/offense/plate-discipline/
 */
class NewPlateDisciplineStatsCalculator extends PlateDisciplineStatsCalculator
{
    constructor(
        hitClassifier, strikeOutClassifier, swingClassifier, whiffClassifier, kZoneClassifier,
        plateAppearanceClassifier
    ) {
       super(
           hitClassifier || defaultHitClassifier,
           strikeOutClassifier || defaultStrikeOutClassifier,
           swingClassifier || defaultSwingClassifier,
           whiffClassifier || defaultWhiffClassifier,
           kZoneClassifier || defaultkZoneClassifier
       );

       this.plateAppearanceClassifier = plateAppearanceClassifier || defaultPlateAppearanceClassifier;
    }

    calculatePlateDiscipline(pitchingResults, includePercentages, pitchingContext) {
        if (!(pitchingResults instanceof Array)) {
            throw new TypeError('pitchingResults must be an instance of array');
        }

        if (typeof(includePercentages) === 'undefined') {
            includePercentages = true;
        }
        else if(typeof(includePercentages) !== 'boolean') {
            throw new TypeError('includePercentages must be an boolean type');
        }

        if (typeof(pitchingContext) === 'undefined') {
            pitchingContext = true;
        }
        else if(typeof(pitchingContext) !== 'boolean') {
            throw new TypeError('pitchingContext must be an boolean type');
        }

        const StatsEnum = pitchingContext ? PitchingStats: BattingStats;

        const plateDiscipline = {
            [StatsEnum.NumberOfPitches.key]: 0,
            KZone: 0,
            OZone: 0,
            [StatsEnum.Strikeouts.key]: 0,
            [StatsEnum.Swings.key]: 0,
            ZSwings: 0,
            ZContacts: 0,
            ZWhiffs: 0,
            OSwings: 0,
            OContacts: 0,
            OWhiffs: 0,
            [StatsEnum.Whiffs.key]: 0,
            [StatsEnum.Contacts.key]: 0,
            PitchesWithStrikeZonePosition: 0,
        };

        if (pitchingContext) {
            plateDiscipline[PitchingStats.BattersFaced.key] = 0;
            plateDiscipline[PitchingStats.HitsAllowed.key] = 0;
        }
        else {
            plateDiscipline[BattingStats.PlateAppearances.key] = 0;
            plateDiscipline[BattingStats.Hits.key] = 0;
        }

        const battersFacedOrPa = [];

        //let's go through pitchingResults
        for (let i = 0; i < pitchingResults.length; i++) {
            const result = pitchingResults[i];
            if (typeof(result) !== 'object' || result === null) {
                throw new TypeError(`Result at index ${i} is not an object or null`);
            }

            const resultData = result[ResultDataNew.Data.key];
            if (typeof(resultData) !== 'object' || resultData === null) {
                throw new TypeError(`Result.Data at index ${i} is not an object or null`);
            }

            const batterSessionPaId = this.getBatterId(result);

            if (!batterSessionPaId) {
                throw new TypeError(`Unable to determine batter/session/PA id at index ${i}`);
            }

            // TODO - better plate appearance check, use PA classifier ?
            if (battersFacedOrPa.indexOf(batterSessionPaId) < 0) {
                battersFacedOrPa.push(batterSessionPaId);
                if (pitchingContext) {
                    plateDiscipline[PitchingStats.BattersFaced.key]++;
                }
                else {
                    plateDiscipline[BattingStats.PlateAppearances.key]++;
                }
            }

            plateDiscipline[StatsEnum.NumberOfPitches.key]++;

            let swing = false, whiff = false;
            const paResult = IterableDataProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
            );
            const pitchCall = IterableDataProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(ScoringData.PitchCall.key, DataSection.Scoring.key)
            );

            if (this.hitClassifier(paResult)) {
                if (pitchingContext) {
                    plateDiscipline[PitchingStats.HitsAllowed.key]++;
                }
                else {
                    plateDiscipline[BattingStats.Hits.key]++;
                }
            }

            if (this.strikeOutClassifier(paResult)) {
                plateDiscipline[StatsEnum.Strikeouts.key]++;
            }

            if (this.swingClassifier(pitchCall)) {
                swing = true;
                plateDiscipline[StatsEnum.Swings.key]++;
            }

            if (this.whiffClassifier(pitchCall)) {
                whiff = true;
                plateDiscipline[StatsEnum.Whiffs.key]++;
            }
            else if (swing) {
                plateDiscipline[StatsEnum.Contacts.key]++;
            }

            const strikeZoneSector = IterableDataProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(PitchReprocessed.StrikeZonePositionR.key, DataSection.Tagging.key, ResultType.Pitch.key)
            );

            if (strikeZoneSector == null) {
                // unknown, skip
                continue;
            }

            plateDiscipline.PitchesWithStrikeZonePosition++;
            if (this.kZoneClassifier(strikeZoneSector)) {
                plateDiscipline.KZone++;

                if (swing) {
                    plateDiscipline.ZSwings++;

                    if (!whiff) {
                        plateDiscipline.ZContacts++;
                    }
                    else {
                        plateDiscipline.ZWhiffs++;
                    }
                }
            }
            else {
                plateDiscipline.OZone++;

                if (swing) {
                    plateDiscipline.OSwings++;

                    if (!whiff) {
                        plateDiscipline.OContacts++;
                    }
                    else {
                        plateDiscipline.OWhiffs++;
                    }
                }
            }
        }

        if (includePercentages) {
           return Object.assign(plateDiscipline, this.calculatePlateDisciplinePercentages(plateDiscipline, pitchingContext));
        }

        return plateDiscipline;
    }

    getBatterId(result) {
        const dataPath = [
            [ResultDataNew.SessionId.key],
            [ResultDataNew.BatterId.key],
            [ResultDataNew.Data.key, DataSection.Scoring.key, PitchReprocessed.PlateAppearanceR.key]
        ];
        const id = [];
        for (const path of dataPath) {
            const value = IterableDataProcessor.getObjectContent(result, path);
            if (typeof(value) === 'undefined') {
                // TODO - log ?
                return false;
            }
            id.push(value);
        }

        return id.join('-');
    }

    calculatePlateDisciplinePercentages(plateDisciplineTotals, pitchingContext) {
        if (!plateDisciplineTotals || typeof(plateDisciplineTotals) !== 'object') {
            throw new TypeError('plateDisciplineTotals must be an object compatible with output generated by calculatePlateDiscipline method');
        }

        if (typeof(pitchingContext) === 'undefined') {
            pitchingContext = true;
        }
        else if(typeof(pitchingContext) !== 'boolean') {
            throw new TypeError('pitchingContext must be an boolean type');
        }

        const StatsEnum = pitchingContext ? PitchingStats: BattingStats;

        const plateDiscipline = {
            [StatsEnum.KZoneRate.key]: NaN,
            [StatsEnum.StrikeoutRate.key]: NaN,
            [StatsEnum.SwingRate.key]: NaN,
            [StatsEnum.ZSwingRate.key]: NaN,
            [StatsEnum.ZContactRate.key]: NaN,
            [StatsEnum.ZWhiffRate.key]: NaN,
            [StatsEnum.OSwingRate.key]: NaN,
            [StatsEnum.OContactRate.key]: NaN,
            [StatsEnum.OWhiffRate.key]: NaN,
            [StatsEnum.WhiffRate.key]: NaN,
            [StatsEnum.SwingingStrikeRate.key]: NaN,
            [StatsEnum.ContactRate.key]: NaN,
        };
        
        // @see https://library.fangraphs.com/offense/plate-discipline/
        // @see also https://www.fantraxhq.com/sabermetric-series-part-3-plate-discipline/
        if (plateDisciplineTotals[StatsEnum.NumberOfPitches.key]) {
            // Swing% (overall percentage of the time a batter swings, per pitch)
            plateDiscipline[StatsEnum.SwingRate.key] = 100 * plateDisciplineTotals[StatsEnum.Swings.key] / plateDisciplineTotals[StatsEnum.NumberOfPitches.key];

            // SwStr% (aka Swinging Strike Rate; percentage of swings that do not result in contact)
            // https://rotofanatic.com/glossary/swstr/
            // https://www.newsbreak.com/news/2046770176912/swstr-leaders
            // https://www.reddit.com/r/fantasybaseball/comments/b2nwvb/whats_the_difference_between_whiff_and_swstr/
            // sometimes it can be incorrectly taken as whiff rate, see https://www.fantasypros.com/2020/02/sabermetrics-glossary-swinging-strike-rate/
            plateDiscipline[StatsEnum.SwingingStrikeRate.key] = 100 * plateDisciplineTotals[StatsEnum.Whiffs.key] / plateDisciplineTotals[StatsEnum.NumberOfPitches.key];

            if (plateDisciplineTotals.PitchesWithStrikeZonePosition) {
                // Zone% (percentage of pitches the batter gets inside the strike zone)
                plateDiscipline[StatsEnum.KZoneRate.key] = 100 * plateDisciplineTotals.KZone / plateDisciplineTotals[StatsEnum.NumberOfPitches.key];
            }

            if (plateDisciplineTotals[StatsEnum.Swings.key]) {
                // Whiff rate looks at swings and misses as a percentage of swings, while SwStr looks at swings and misses as a percentage of all pitches thrown.
                plateDiscipline[StatsEnum.WhiffRate.key] = 100 * plateDisciplineTotals[StatsEnum.Whiffs.key] / plateDisciplineTotals[StatsEnum.Swings.key];

                // @see https://www.insiderbaseball.com/blog/2019/03/an_introduction_to_contact_the_shohei_ohtani_of_sabermetrics.html
                // Contact% (overall percentage of the time a batter makes contact, per swing)
                plateDiscipline[StatsEnum.ContactRate.key] = 100 * plateDisciplineTotals[StatsEnum.Contacts.key] / plateDisciplineTotals[StatsEnum.Swings.key];
            }

            if (plateDisciplineTotals.OSwings) {
                // O-Whiff% - Percentage of the time a batter swings and misses on a pitch outside the strike zone per swing.
                plateDiscipline[StatsEnum.OWhiffRate.key] = 100 * plateDisciplineTotals.OWhiffs / plateDisciplineTotals.OSwings;

                // O-Contact% (percentage of the time a batter makes contact on a pitch outside the strike zone per swing)
                plateDiscipline[StatsEnum.OContactRate.key] = 100 * plateDisciplineTotals.OContacts / plateDisciplineTotals.OSwings;
            }

            if (plateDisciplineTotals.ZSwings) {
                // Z-Whiff% - Percentage of the time a batter swings and misses on a pitch inside the strike zone per swing.
                plateDiscipline[StatsEnum.ZWhiffRate.key] = 100 * plateDisciplineTotals.ZWhiffs / plateDisciplineTotals.ZSwings;

                // Z-Contact% (percentage of the time a batter makes contact on a pitch inside the strike zone per swing)
                plateDiscipline[StatsEnum.ZContactRate.key] = 100 * plateDisciplineTotals.ZContacts / plateDisciplineTotals.ZSwings;
            }

            if (plateDisciplineTotals.OZone) {
                // O-Swing% (percentage of the time a batter swings at pitches outside the strike zone; also referred to as “Chase Rate”)
                plateDiscipline[StatsEnum.OSwingRate.key] = 100 * plateDisciplineTotals.OSwings / plateDisciplineTotals.OZone;
            }

            if (plateDisciplineTotals.KZone) {
                // Z-Swing% (percentage of the time a batter swings at pitches inside the strike zone)
                plateDiscipline[StatsEnum.ZSwingRate.key] = 100 * plateDisciplineTotals.ZSwings / plateDisciplineTotals.KZone;
            }

            if (pitchingContext && plateDisciplineTotals[PitchingStats.BattersFaced.key]) {
                // Represents the frequency with which a pitcher strikes out hitters, as determined by total strikeouts divided by total batters faced.
                plateDiscipline[PitchingStats.StrikeoutRate.key] = 100 * plateDisciplineTotals[PitchingStats.Strikeouts.key] / plateDisciplineTotals[PitchingStats.BattersFaced.key];
            }

            if (!pitchingContext && plateDisciplineTotals[BattingStats.PlateAppearances.key]) {
                // Represents the frequency with which pitchers strikes out hitter, as determined by total strikeouts divided by number of plate appearances.
                plateDiscipline[BattingStats.StrikeoutRate.key] = 100 * plateDisciplineTotals[BattingStats.Strikeouts.key] / plateDisciplineTotals[BattingStats.PlateAppearances.key];
            }
        }

        return plateDiscipline;
    }
}

export default NewPlateDisciplineStatsCalculator;